import { cn } from '@goaltree/ui/lib/utils'
import { useForm } from '@tanstack/react-form'
import { type ReactNode, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { caretOffsetFromPoint } from '@/lib/caret'

const titleSchema = z.string().min(1, 'Title cannot be empty')

export type InlineEditorHandle = {
  startEdit: () => void
  startEditAtPoint: (x: number, y: number) => void
}

type InlineEditorProps = {
  value: string
  displayValue?: ReactNode
  onSave: (title: string) => Promise<unknown>
  onEmpty?: () => void
  onEditingChange?: (editing: boolean) => void
  onValueChange?: (v: string) => void
  className?: string
  disabled?: boolean
  maxLength?: number
  as?: 'span' | 'p'
}

export const InlineEditor = forwardRef<InlineEditorHandle, InlineEditorProps>(function InlineEditor(
  {
    value,
    displayValue,
    onSave,
    onEmpty,
    onEditingChange,
    onValueChange,
    className,
    disabled,
    maxLength,
    as: Tag = 'span',
  },
  ref,
) {
  const [editing, setEditing] = useState(false)
  const tapPositionRef = useRef<{ x: number; y: number } | null>(null)

  function setEditingWithCallback(next: boolean) {
    setEditing(next)
    onEditingChange?.(next)
  }

  useImperativeHandle(ref, () => ({
    startEdit: () => setEditingWithCallback(true),
    startEditAtPoint: (x: number, y: number) => {
      tapPositionRef.current = { x, y }
      setEditingWithCallback(true)
    },
  }))

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const form = useForm({
    defaultValues: { title: value },
    validators: { onSubmit: z.object({ title: titleSchema }) },
    onSubmit: async ({ value: formValue }) => {
      try {
        await onSave(formValue.title)
        setEditingWithCallback(false)
      } catch {
        toast.error("Couldn't save — please try again.")
      }
    },
  })

  // Keep form value in sync with value prop when not editing
  useEffect(() => {
    if (!editing) {
      form.reset({ title: value })
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  // Focus and scroll into view when entering edit mode
  useEffect(() => {
    const el = textareaRef.current
    if (editing && el) {
      el.focus()
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      const pos = tapPositionRef.current
      if (pos) {
        tapPositionRef.current = null
        const offset = caretOffsetFromPoint(el, pos.x, pos.y)
        el.setSelectionRange(offset, offset)
      }
    }
  }, [editing])

  if (!editing) {
    return (
      <Tag className={className}>
        {disabled ? (
          (displayValue ?? value)
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              tapPositionRef.current = { x: e.clientX, y: e.clientY }
              setEditingWithCallback(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setEditingWithCallback(true)
            }}
            className="cursor-text decoration-dashed decoration-muted-foreground/40 decoration-1 underline-offset-4 hover:underline"
          >
            {displayValue ?? value}
          </span>
        )}
      </Tag>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'contents' }}
    >
      <form.Field name="title">
        {(field) => (
          <textarea
            ref={textareaRef}
            rows={1}
            maxLength={maxLength}
            value={field.state.value}
            onChange={(e) => {
              field.handleChange(e.target.value)
              onValueChange?.(e.target.value)
            }}
            onBlur={() => {
              field.handleBlur()
              if (field.state.value.trim() === '' && onEmpty) {
                form.reset()
                setEditingWithCallback(false)
                onEmpty()
              } else if (
                field.state.value !== value &&
                (!maxLength || field.state.value.length <= maxLength)
              ) {
                form.handleSubmit()
              } else if (!maxLength || field.state.value.length <= maxLength) {
                setEditingWithCallback(false)
              }
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Escape') {
                form.reset()
                setEditingWithCallback(false)
              } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (field.state.value.trim() === '' && onEmpty) {
                  form.reset()
                  setEditingWithCallback(false)
                  onEmpty()
                } else if (!maxLength || field.state.value.length <= maxLength) {
                  form.handleSubmit()
                }
              }
            }}
            className={cn(
              className,
              'block w-full resize-none overflow-hidden bg-transparent border-none outline-none ring-0 p-0 m-0 [field-sizing:content]',
            )}
          />
        )}
      </form.Field>
    </form>
  )
})
